// F1a (M40): sedes (centros de trabajo) de la empresa. K1: el Checklist sigue POR EMPRESA; las sedes son un catálogo
// para asignar y filtrar. Toda empresa tiene su Matriz; con una sola sede activa la app se ve igual que antes (sin
// selectores de sede). Aquí está la lógica pura; la pantalla es src/pages/SedesPage.tsx.

import { ESTADOS_REGISTRADOS } from './trabajadores-registrados';
import { getCompanySize, getParitarioType, getSHTRequired, type RiskLevel } from './sst-rules';

export type TipoSede = 'matriz' | 'sucursal';

export interface FilaSede {
  id: string;
  company_id: string;
  name: string;
  site_type: TipoSede;
  is_main: boolean;
  address: string | null;
  canton_code: string | null;
  parish: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  responsible_name: string | null;
  distance_to_nearest_town_km: number | null;
  workers_declared: number | null;
  active: boolean;
}

export const CAMPOS_SEDE = 'id, company_id, name, site_type, is_main, address, canton_code, parish, latitude, longitude, phone, '
  + 'responsible_name, distance_to_nearest_town_km, workers_declared, active';

/** Matriz primero; después las sucursales por nombre. */
export function ordenarSedes<T extends Pick<FilaSede, 'is_main' | 'name'>>(sedes: T[]): T[] {
  return [...sedes].sort((a, b) => (a.is_main === b.is_main ? a.name.localeCompare(b.name, 'es') : a.is_main ? -1 : 1));
}

export const sedesActivas = <T extends Pick<FilaSede, 'active'>>(sedes: T[]) => sedes.filter(s => s.active);

/** Los selectores y filtros de sede aparecen solo con 2 o más sedes activas. */
export const muestraSelectorSede = (sedes: Pick<FilaSede, 'active'>[]) => sedesActivas(sedes).length >= 2;

/** Opciones del selector: las activas y, si la fila ya está en una sede inactiva, también esa (para no perderla). */
export function opcionesSede<T extends Pick<FilaSede, 'id' | 'active' | 'is_main' | 'name'>>(sedes: T[], actual?: string | null): T[] {
  return ordenarSedes(sedes.filter(s => s.active || s.id === actual));
}

export const matrizDe = <T extends Pick<FilaSede, 'is_main'>>(sedes: T[]) => sedes.find(s => s.is_main) ?? null;

export const etiquetaSede = (s: Pick<FilaSede, 'name' | 'active'>) => s.active ? s.name : `${s.name} (inactiva)`;

// ── Registrados por sede (informativo, como el trigger de M39-bis: activo + licencia) ──
export function registradosPorSede(ws: { site_id?: string | null; status: string | null }[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const w of ws) {
    if (!w.site_id || !(ESTADOS_REGISTRADOS as readonly string[]).includes(w.status ?? '')) continue;
    m.set(w.site_id, (m.get(w.site_id) ?? 0) + 1);
  }
  return m;
}

// ── Avisos de la pantalla de Sedes ──
/** Con varias sedes activas, la suma de los declarados por sede frente al declarado del Perfil SSO (sin recalcular). */
export function avisoSumaDeclarados(sedes: Pick<FilaSede, 'active' | 'workers_declared'>[], declaradoEmpresa: number | null | undefined): string | null {
  const activas = sedesActivas(sedes);
  if (activas.length < 2 || declaradoEmpresa == null) return null;
  const suma = activas.reduce((t, s) => t + (s.workers_declared ?? 0), 0);
  return suma === declaradoEmpresa ? null
    : `La suma de tus sedes (${suma}) no coincide con lo declarado en tu Perfil SSO (${declaradoEmpresa}).`;
}

export interface AvisoCentros { texto: string; difiere: boolean }

/** work_centers_count sigue siendo DECLARADO (no se pisa): se muestra junto a las sedes registradas. */
export function avisoCentros(declarados: number | null | undefined, sedes: Pick<FilaSede, 'active'>[]): AvisoCentros | null {
  if (declarados == null) return null;
  const y = sedesActivas(sedes).length;
  const pl = (k: number, uno: string, varios: string) => `${k} ${k === 1 ? uno : varios}`;
  return {
    texto: `Declaraste ${pl(declarados, 'centro de trabajo', 'centros de trabajo')}; registraste ${pl(y, 'sede', 'sedes')}.`,
    difiere: declarados !== y,
  };
}

// ── Quitar una sede ──
export type AccionQuitar = 'ninguna' | 'borrar' | 'desactivar';

/** Lo que la sede tiene asignado. Inspecciones, programaciones y hallazgos: motor de inspecciones (F3a, M41). */
export interface UsoSede {
  trabajadores: number; bodegas: number; plan: boolean;
  inspecciones: number; programaciones: number; hallazgos: number;
}

/** La Matriz no se quita; otra sede se borra solo si no tiene nada asignado (como el trigger de M41); si lo tiene, se desactiva. */
export function accionQuitar(sede: Pick<FilaSede, 'is_main'>, uso: UsoSede): AccionQuitar {
  if (sede.is_main) return 'ninguna';
  return uso.trabajadores > 0 || uso.bodegas > 0 || uso.plan || uso.inspecciones > 0 || uso.programaciones > 0 || uso.hallazgos > 0
    ? 'desactivar' : 'borrar';
}

export function mensajeQuitar(nombre: string, uso: UsoSede): string {
  const partes: string[] = [];
  if (uso.trabajadores > 0) partes.push(`${uso.trabajadores} ${uso.trabajadores === 1 ? 'trabajador' : 'trabajadores'}`);
  if (uso.bodegas > 0) partes.push(`${uso.bodegas} ${uso.bodegas === 1 ? 'bodega' : 'bodegas'}`);
  if (uso.plan) partes.push('su plan de emergencias');
  if (uso.inspecciones > 0) partes.push(`${uso.inspecciones} ${uso.inspecciones === 1 ? 'inspección' : 'inspecciones'}`);
  if (uso.programaciones > 0) partes.push(`${uso.programaciones} ${uso.programaciones === 1 ? 'programación de inspecciones' : 'programaciones de inspecciones'}`);
  if (uso.hallazgos > 0) partes.push(`${uso.hallazgos} ${uso.hallazgos === 1 ? 'hallazgo' : 'hallazgos'}`);
  if (partes.length === 0) return `"${nombre}" no tiene trabajadores, bodegas, plan de emergencias ni inspecciones. Se borrará; no se puede deshacer.`;
  return `"${nombre}" tiene ${partes.join(', ')}: no se puede borrar. Se desactivará: deja de aparecer en las listas y en los `
    + 'selectores, y lo que tiene asignado se conserva. Puedes reactivarla después.';
}

// ── Formulario ──
export interface FormSede {
  name: string; address: string; canton_code: string | null; parish: string; latitude: string; longitude: string;
  phone: string; responsible_name: string; distance_to_nearest_town_km: string; workers_declared: string;
}

export const FORM_VACIO: FormSede = {
  name: '', address: '', canton_code: null, parish: '', latitude: '', longitude: '', phone: '', responsible_name: '',
  distance_to_nearest_town_km: '', workers_declared: '',
};

const aTexto = (n: number | null | undefined) => (n == null ? '' : String(n).replace('.', ','));

export function formDesdeSede(s: FilaSede): FormSede {
  return {
    name: s.name, address: s.address ?? '', canton_code: s.canton_code, parish: s.parish ?? '',
    latitude: aTexto(s.latitude), longitude: aTexto(s.longitude), phone: s.phone ?? '', responsible_name: s.responsible_name ?? '',
    distance_to_nearest_town_km: aTexto(s.distance_to_nearest_town_km), workers_declared: s.workers_declared == null ? '' : String(s.workers_declared),
  };
}

/** Número con coma o punto decimal; vacío = null; inválido = NaN. */
export function leerNumero(t: string): number | null {
  const s = t.trim().replace(/\s/g, '');
  if (s === '') return null;
  return /^-?\d+([.,]\d+)?$/.test(s) ? Number(s.replace(',', '.')) : NaN;
}

export type DatosSede = Omit<FilaSede, 'id' | 'company_id' | 'site_type' | 'is_main' | 'active'>;

/** Valida el formulario (los mismos límites que los CHECK de M40) y devuelve los datos o los errores por campo. */
export function validarSede(f: FormSede, otrosNombres: string[]): { datos: DatosSede | null; errores: Partial<Record<keyof FormSede, string>> } {
  const e: Partial<Record<keyof FormSede, string>> = {};
  const name = f.name.trim();
  if (!name) e.name = 'El nombre es obligatorio.';
  else if (name.length > 120) e.name = 'Máximo 120 caracteres.';
  else if (otrosNombres.some(n => n.trim().toLowerCase() === name.toLowerCase())) e.name = 'Ya hay una sede con ese nombre.';
  if (f.address.trim().length > 300) e.address = 'Máximo 300 caracteres.';
  if (f.canton_code !== null && !/^[0-9]{4}$/.test(f.canton_code)) e.canton_code = 'Código de cantón no válido.';
  if (f.parish.trim().length > 120) e.parish = 'Máximo 120 caracteres.';
  if (f.phone.trim().length > 40) e.phone = 'Máximo 40 caracteres.';
  if (f.responsible_name.trim().length > 160) e.responsible_name = 'Máximo 160 caracteres.';
  const lat = leerNumero(f.latitude), lon = leerNumero(f.longitude);
  const km = leerNumero(f.distance_to_nearest_town_km), dec = leerNumero(f.workers_declared);
  if (lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90)) e.latitude = 'Latitud entre -90 y 90 (por ejemplo -0,1807).';
  if (lon !== null && (Number.isNaN(lon) || lon < -180 || lon > 180)) e.longitude = 'Longitud entre -180 y 180 (por ejemplo -78,4678).';
  if (km !== null && (Number.isNaN(km) || km < 0)) e.distance_to_nearest_town_km = 'Distancia en km, 0 o más (usa coma decimal).';
  if (dec !== null && (Number.isNaN(dec) || dec < 0 || !Number.isInteger(dec))) e.workers_declared = 'Número entero, 0 o más.';
  if (Object.keys(e).length > 0) return { datos: null, errores: e };
  const txt = (s: string) => s.trim() || null;
  return {
    errores: {},
    datos: {
      name, address: txt(f.address), canton_code: f.canton_code, parish: txt(f.parish), latitude: lat, longitude: lon,
      phone: txt(f.phone), responsible_name: txt(f.responsible_name), distance_to_nearest_town_km: km, workers_declared: dec,
    },
  };
}

/** Errores de la base (triggers y CHECK de M40) → texto para la pantalla. */
export function mensajeErrorSede(error: { message?: string; code?: string } | null | undefined): string {
  const m = error?.message ?? '';
  if (/company_sites_nombre_key/.test(m)) return 'Ya hay una sede con ese nombre.';
  if (/row-level security|permission denied/i.test(m)) return 'No tienes permiso para hacer esto (crear y editar: administrador o gerente; borrar y desactivar: administrador).';
  if (/La Matriz|Solo el administrador|no se puede borrar|no es de esta empresa|ya tiene su Matriz|no cambia de empresa/.test(m)) return m;
  if (/company_sites|42P01|PGRST205/.test(m) || error?.code === '42P01' || error?.code === 'PGRST205') return 'Las sedes todavía no están disponibles: falta actualizar la base de datos. Avisa al soporte de StorSafe.'; // M40
  return m || 'No se pudo guardar. Intenta de nuevo.';
}

// ── Sincronización Matriz → Perfil SSO (solo con una sede activa) ──
/**
 * Al editar el declarado de la Matriz con una sola sede activa, el Perfil SSO toma ese número y, con las mismas reglas
 * del onboarding (StepCIIU), el tamaño, el organismo paritario y la figura SST. El sentido Perfil → Matriz lo hace la
 * base (trigger de M40).
 */
export function perfilDesdeDeclarado(n: number, riesgo: RiskLevel | null | undefined) {
  const company_size = getCompanySize(n);
  return {
    workers_count: n,
    company_size,
    paritario_type: getParitarioType(n),
    ...(riesgo ? { sht_required: getSHTRequired(company_size, riesgo) } : {}),
  };
}

/** ¿Hay que copiar el declarado de la Matriz al Perfil SSO? Solo con una sede activa y si cambió. */
export function sincronizaMatrizConPerfil(sede: Pick<FilaSede, 'is_main'>, sedes: Pick<FilaSede, 'active'>[],
  nuevo: number | null, perfil: { workers_count: number | null } | null): boolean {
  return sede.is_main && sedesActivas(sedes).length === 1 && nuevo !== null && !!perfil && perfil.workers_count !== nuevo;
}

// ── Plan de Emergencias v2, paso 1 (solo lectura en F1a) ──
export type AvisoCantonPlan = { tipo: 'copiar'; canton: string } | { tipo: 'difiere'; canton: string } | null;

/** Si la sede tiene cantón y el paso 1 no, se ofrece copiarlo; si difieren, se avisa. */
export function avisoCantonPlan(sedeCanton: string | null | undefined, planCanton: string | null | undefined): AvisoCantonPlan {
  if (!sedeCanton) return null;
  if (!planCanton) return { tipo: 'copiar', canton: sedeCanton };
  return sedeCanton === planCanton ? null : { tipo: 'difiere', canton: sedeCanton };
}
